import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { EditIconGeneral } from '@/app/common/icons/general'
import { parseRequestError } from '@/app/common/utilities'
import { usePutProgramCatalogItem } from '@/queries/program_catalog/hooks/usePutProgramCatalogItem'
import { ProgramItem } from '@/queries/program_catalog/schema'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import * as z from 'zod'

// Define the form schema with Zod
const formSchema = z.object({
  serviceFee: z
    .string()
    .min(1, { message: 'Service Fee is required' })
    .refine((val) => !isNaN(parseFloat(val)), {
      message: 'Service Fee must be a number',
    }),
})

// Type for form values
type FormValues = z.infer<typeof formSchema>

type Props = {
  item: ProgramItem
  onEditSuccess: () => void
}

export default function EditItemPopup(props: Props) {
  const { item, onEditSuccess } = props
  const [isOpen, setIsOpen] = React.useState(false)
  const { toast } = useToast()
  const programId = useSelector(selectSelectedProgram).id

  // Initialize React Hook Form with Zod resolver
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      serviceFee: item.service_fee.toString(),
    },
  })

  const updateItemMutation = usePutProgramCatalogItem({
    onSuccess: () => {
      onEditSuccess()
      toast({
        title: 'Item updated',
        description: `${item.global_item_name} has been updated.`,
        variant: 'default',
      })
      onEditSuccess()
      setIsOpen(false)
    },
    onError: (error) => {
      toast({
        title: 'Error updating item',
        description: parseRequestError(error).message,
        variant: 'destructive',
      })
    },
  })

  // Handle dialog open state changes
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      reset({
        serviceFee: item.service_fee.toString(),
      })
    }
  }

  // Form submission handler
  const onSubmit = (values: FormValues) => {
    const serviceFee = parseFloat(values.serviceFee)

    updateItemMutation.mutate({
      itemId: item.id,
      programId: programId,
      service_fee: serviceFee,
    })
  }

  // Custom handler for service fee input to only allow numeric values
  const handleServiceFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Allow empty string, digits, and decimal point
    if (value === '' || /^(\d+\.?\d*|\.\d+)$/.test(value)) {
      e.target.value = value
    } else {
      e.preventDefault()
    }
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      <DialogTrigger asChild>
        <button className="w-full flex items-center gap-2 hover:bg-gray-100 text-left transition rounded px-2 py-2">
          <EditIconGeneral /> Edit
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Item</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
          <div className="space-y-1">
            <Label htmlFor="serviceFee">
              Service Fee (%)<span className="text-red-500">*</span>
            </Label>
            <Input
              id="serviceFee"
              {...register('serviceFee')}
              placeholder="Enter service fee percentage"
              className={errors.serviceFee ? 'border-red-500' : ''}
              onChange={handleServiceFeeChange}
            />
            {errors.serviceFee && (
              <p className="text-red-500 text-sm">
                {errors.serviceFee.message}
              </p>
            )}
          </div>

          <div className="text-sm text-gray-500">
            <p>Update the service fee for {item.global_item_name}.</p>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <div>
              <SubmitButton
                status={updateItemMutation.status}
                buttonTxt="Save Changes"
              />
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
